import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router';
import { DocViewer } from '../../components/review/DocViewer';
import { VerifyProgress } from '../../components/review/VerifyProgress';
import { CountrySelector, type Country, type KrProfile } from '../../components/review/CountrySelector';
import { Alert, Badge, Button, Card, Input, Select } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import type { Check, Document, Finding } from '../../lib/types';

const sevTone = (s: Finding['severity']) =>
  (s === 'critical' ? 'red' : s === 'major' ? 'amber' : s === 'info' ? 'blue' : 'green') as
    'red' | 'amber' | 'blue' | 'green';
const REVIEWABLE = new Set(['label_artwork', 'label_copy', 'sfp']);

/** Annotated review workspace (tech design 14.3): viewer with pins + finding cards + approval actions (14.4). */
export function DocumentReviewPage() {
  const { t } = useTranslation();
  const { pid, docId } = useParams();
  const { identity } = useSession();
  const orgId = identity?.org?.id;
  const roles = identity?.roles ?? [];
  const sa = !!identity?.superAdmin;
  const canVerify = sa || roles.some((r) => ['admin', 'ra', 'qa', 'design'].includes(r));
  const canOverride = sa || roles.some((r) => ['admin', 'ra'].includes(r));
  const base = `/api/v1/orgs/${orgId}/products/${pid}`;
  const [doc, setDoc] = useState<Document | null>(null);
  const [check, setCheck] = useState<Check | null>(null);
  const [countries, setCountries] = useState<Country[]>(['us-fda']);
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [countryReady, setCountryReady] = useState(true);
  const [krProfile, setKrProfile] = useState<KrProfile>({
    productClass: 'unknown',
    context: 'label',
    servingsPerDay: 1,
  });
  const loadedSelection = useRef<string | null>(null);
  useEffect(() => {
    setCountries(['us-fda']);
    setCountryFilter('all');
    setKrProfile({ productClass: 'unknown', context: 'label', servingsPerDay: 1 });
    loadedSelection.current = null;
  }, [docId]);
  useEffect(() => {
    if (check && check.input.docId === docId && loadedSelection.current !== docId) {
      setCountries(check.jurisdictions ?? ['us-fda']);
      const p = check.regulatoryProfiles?.['kr-mfds'];
      if (p)
        setKrProfile({
          productClass: p.productClass as KrProfile['productClass'],
          context: p.context,
          servingsPerDay: p.servingsPerDay ?? 1,
        });
      loadedSelection.current = docId ?? null;
    }
  }, [check, docId]);
  const [prev, setPrev] = useState<Check | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'critical' | 'major' | 'info' | 'ok'>('all');
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [itemNote, setItemNote] = useState<Record<string, string>>({});
  const [callouts, setCallouts] = useState(true);
  const [finished, setFinished] = useState<Check | null>(null); // "review complete" banner after a run
  const viewerRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    const d = await api<Document>(`${base}/documents/${docId}`);
    setDoc(d);
    const currentCheck = d.activeCheckId ?? d.latestCheckId;
    if (currentCheck) setCheck(await api<Check>(`${base}/checks/${currentCheck}`));
    else {
      const list = await api<{ checks: Check[] }>(`${base}/documents/${docId}/checks`);
      setCheck(list.checks[0] ? await api<Check>(`${base}/checks/${list.checks[0].id}`) : null);
    }
    if (d.supersedesDocId) {
      const p = await api<Document>(`${base}/documents/${d.supersedesDocId}`).catch(() => null);
      setPrev(
        p?.latestCheckId ? await api<Check>(`${base}/checks/${p.latestCheckId}`).catch(() => null) : null,
      );
    }
  }, [base, docId, orgId]);
  useEffect(() => {
    void load().catch((e: unknown) => setErr(String(e)));
  }, [load]);
  const running = doc?.status === 'verifying' || check?.status === 'queued' || check?.status === 'running';
  // the report is rendered after the result is stored -> keep polling briefly until its download link exists
  const reportPending =
    (check?.status === 'done' || (check?.status === 'failed' && !!check.result)) &&
    !check.report &&
    !!check.finishedAt &&
    Date.now() - new Date(check.finishedAt).getTime() < 3 * 60_000;
  useEffect(() => {
    if (!running && !reportPending) return;
    const id = setTimeout(() => void load().catch(() => undefined), running ? 3000 : 5000);
    return () => clearTimeout(id);
  }, [running, reportPending, load, doc, check]);
  // running -> done: show the completion banner, switch callouts on and bring the annotated document into view
  const wasRunning = useRef(false);
  useEffect(() => {
    if (running) {
      wasRunning.current = true;
      setFinished(null);
    } else if (wasRunning.current && check?.status === 'done' && check.result) {
      wasRunning.current = false;
      setFinished(check);
      setCallouts(true);
      setFilter('all');
      viewerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (wasRunning.current && check?.status === 'failed') wasRunning.current = false;
  }, [running, check]);
  useEffect(() => {
    if (selected)
      document.getElementById(`card-${selected}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selected]);

  const fail = (e: unknown) =>
    setErr(
      e instanceof ApiError
        ? t([`review.err_${e.code}`, 'review.err_generic'], { code: e.code }) + ` ${e.message}`
        : String(e),
    );
  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    setErr(null);
    try {
      await api(`${base}/documents/${docId}/${path}`, { method: 'POST', json: body ?? {} });
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const dispose = async (item: Finding, disposition: Finding['disposition']) => {
    if (!check) return;
    try {
      const r = await api<{ result: Check['result'] }>(`${base}/checks/${check.id}/items/${item.id}`, {
        method: 'PATCH',
        json: { disposition, note: itemNote[item.id] ?? null },
      });
      setCheck({ ...check, result: r.result });
      await load();
    } catch (e) {
      fail(e);
    }
  };
  const items = useMemo(() => {
    const all = check?.result?.items ?? [];
    const order = { critical: 0, major: 1, info: 2, ok: 3 };
    return all
      .filter((i) => filter === 'all' || i.severity === filter)
      .sort((a, b) => order[a.severity] - order[b.severity]);
  }, [check, filter]);
  // one stable number per actionable finding (same on the box, the callout and the card), by severity then position
  const numbers = useMemo(() => {
    const order = { critical: 0, major: 1, info: 2, ok: 3 };
    const list = (check?.result?.items ?? [])
      .filter((i) => i.severity !== 'ok' && i.disposition !== 'overridden')
      .sort(
        (a, b) =>
          order[a.severity] - order[b.severity] ||
          (a.location?.page ?? 99) - (b.location?.page ?? 99) ||
          (a.location?.bbox?.[1] ?? 2) - (b.location?.bbox?.[1] ?? 2),
      );
    return Object.fromEntries(list.map((f, i) => [f.id, i + 1])) as Record<string, number>;
  }, [check]);
  const compare = useMemo(() => {
    if (!prev?.result || !check?.result) return null;
    const key = (f: Finding) => `${f.jurisdictionId ?? 'us-fda'}|${f.subject}|${f.severity}`.toLowerCase();
    const before = new Set(prev.result.items.filter((f) => f.severity !== 'ok').map(key));
    const after = new Set(check.result.items.filter((f) => f.severity !== 'ok').map(key));
    return {
      resolved: [...before].filter((k) => !after.has(k)).length,
      added: [...after].filter((k) => !before.has(k)).length,
      unchanged: [...after].filter((k) => before.has(k)).length,
    };
  }, [prev, check]);

  if (identity && identity.state !== 'member') return <Navigate to="/" replace />;
  if (!doc) return err ? <Alert>{err}</Alert> : <p className="text-slate-500">{t('app.loading')}</p>;
  const reviewable = REVIEWABLE.has(doc.kind);
  const isUploader = identity?.user.id === doc.uploadedBy;
  const toFix = Object.keys(numbers).length;
  const positionsOff = check?.positions?.provider === 'none';
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Link to={`/products/${pid}?tab=documents`} className="text-sm text-emerald-700 underline">
          ← {t('review.back')}
        </Link>
        <h1 className="text-xl font-semibold">{doc.name}</h1>
        <span className="font-mono text-xs text-slate-500">{doc.id}</span>
        <Badge
          tone={
            doc.status === 'pass'
              ? 'green'
              : doc.status === 'not_compliant' || doc.status === 'failed'
                ? 'red'
                : doc.status === 'verifying'
                  ? 'blue'
                  : 'slate'
          }
        >
          {t(`docs.status_${doc.status}`)}
        </Badge>
        <Badge
          tone={
            doc.approval.state === 'approved'
              ? 'green'
              : doc.approval.state === 'submitted'
                ? 'blue'
                : doc.approval.state === 'changes_requested'
                  ? 'amber'
                  : 'slate'
          }
        >
          {t(`review.approval_${doc.approval.state}`)}
        </Badge>
        {check?.result && (
          <span className="text-sm text-slate-600">
            {check.result.counts.critical ?? 0} {t('review.critical').toLowerCase()} ·{' '}
            {check.result.counts.major ?? 0} {t('review.major').toLowerCase()} ·{' '}
            {check.result.counts.info ?? 0} {t('review.info').toLowerCase()}
          </span>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          {(check?.report?.orgFileId || check?.report?.masterFileId) && (
            <a
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
              href={`${base}/checks/${check.id}/report?download=1`}
            >
              {check.status === 'failed'
                ? `${t('markets.incomplete')} · ${t('review.report')}`
                : check.result?.status === 'pass'
                  ? t('review.report')
                  : t('review.fixReport')}
            </a>
          )}
          {canVerify && reviewable && !doc.supersededByDocId && (
            <Button
              onClick={() =>
                void act('verify', {
                  jurisdictions: countries,
                  ...(countries.includes('kr-mfds') ? { regulatoryProfiles: { 'kr-mfds': krProfile } } : {}),
                })
              }
              disabled={busy || !!running || !countryReady}
            >
              {running ? t('review.verifying') : check ? t('review.again') : t('markets.verify')}
            </Button>
          )}
        </div>
      </div>
      {err && <Alert>{err}</Alert>}
      {canVerify && reviewable && (
        <CountrySelector
          value={countries}
          onChange={setCountries}
          profile={krProfile}
          onProfile={setKrProfile}
          disabled={busy || !!running}
          onReady={setCountryReady}
        />
      )}
      {check && (
        <p className="text-xs text-slate-600">
          {t('markets.reviewed')}:{' '}
          {(check.jurisdictions ?? ['us-fda'])
            .map((x) => (x === 'us-fda' ? 'FDA(US)' : 'MFDS(KR)'))
            .join(' + ')}
        </p>
      )}
      {running && check?.currentJurisdiction && (
        <Badge tone="blue">{check.currentJurisdiction === 'kr-mfds' ? 'MFDS(KR)' : 'FDA(US)'}</Badge>
      )}
      {check?.countryResults && (
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={() => setCountryFilter('all')}>
            {t('review.all')}
          </Button>
          {Object.entries(check.countryResults).map(([id, c]) => (
            <Button key={id} variant="secondary" onClick={() => setCountryFilter(id)}>
              {id === 'us-fda' ? 'FDA(US)' : 'MFDS(KR)'}:{' '}
              {c.status === 'failed' ? t('markets.incomplete') : c.resultStatus} · {c.kbVersion}
            </Button>
          ))}
          {check.status === 'failed' && canVerify && (
            <Button
              disabled={busy || !!running}
              onClick={() =>
                void (async () => {
                  try {
                    setBusy(true);
                    await api(`${base}/checks/${check.id}/retry-country`, { method: 'POST', json: {} });
                    await load();
                  } catch (e) {
                    fail(e);
                  } finally {
                    setBusy(false);
                  }
                })()
              }
            >
              {t('markets.retry')}
            </Button>
          )}
        </div>
      )}
      {!reviewable && <Alert tone="amber">{t('review.notReviewable')}</Alert>}
      {check?.status === 'failed' && <Alert>{t('check.error', { msg: check.error })}</Alert>}
      {compare && (
        <Alert tone="green">
          {t('review.compare')} ({doc.supersedesDocId}): {compare.resolved} {t('review.resolved')} ·{' '}
          {compare.added} {t('review.new')} · {compare.unchanged} {t('review.unchanged')}
        </Alert>
      )}
      {running && <VerifyProgress check={check} since={check?.createdAt ?? null} />}
      {!running && finished && finished.result && (
        <div
          className={`flex flex-wrap items-center gap-3 rounded-lg border p-3 text-sm ${
            finished.result.status === 'pass'
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
              : 'border-red-300 bg-red-50 text-red-900'
          }`}
          role="status"
        >
          <span className="text-lg">{finished.result.status === 'pass' ? '✓' : '!'}</span>
          <span className="flex-1">
            <span className="font-semibold">
              {finished.result.status === 'pass' ? t('review.donePass') : t('review.doneFail', { n: toFix })}
            </span>{' '}
            {toFix > 0 && t('review.doneHint')}
          </span>
          <Button variant="ghost" onClick={() => setFinished(null)}>
            {t('review.dismiss')}
          </Button>
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <Card>
          <div ref={viewerRef} className="mb-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span>{t('review.zoom')}</span>
            <input
              type="range"
              min={0.5}
              max={2}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
            <label className="ml-2 inline-flex cursor-pointer items-center gap-1 font-medium text-slate-700">
              <input type="checkbox" checked={callouts} onChange={(e) => setCallouts(e.target.checked)} />
              {t('review.callouts')}
            </label>
            <span className="ml-auto">{t('review.legend')}</span>
            {check?.positions && <span>· {t('review.positions', { p: check.positions.provider })}</span>}
          </div>
          {positionsOff && <Alert tone="amber">{t('review.noPos')}</Alert>}
          <div className="max-h-[75vh] overflow-auto rounded border border-slate-200 bg-slate-100 p-3">
            <DocViewer
              fileUrl={`${base}/documents/${docId}/file`}
              mime={doc.mime}
              items={items}
              numbers={numbers}
              selected={selected}
              onSelect={setSelected}
              zoom={zoom}
              callouts={callouts && !!check?.result}
              missingLabel={t('review.missingOnLabel')}
            />
          </div>
        </Card>
        <div className="space-y-3">
          <Card
            title={t('review.findings')}
            actions={
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value as typeof filter)}
                className="w-auto"
              >
                <option value="all">{t('review.all')}</option>
                <option value="critical">{t('review.critical')}</option>
                <option value="major">{t('review.major')}</option>
                <option value="info">{t('review.info')}</option>
                <option value="ok">{t('review.okOnly')}</option>
              </Select>
            }
          >
            {!check && <p className="text-sm text-slate-500">{t('review.noCheck')}</p>}
            <div className="max-h-[60vh] space-y-2 overflow-auto">
              {items
                .filter((f) => countryFilter === 'all' || (f.jurisdictionId ?? 'us-fda') === countryFilter)
                .map((f) => {
                  const num = numbers[f.id];
                  return (
                    <div
                      key={f.id}
                      id={`card-${f.id}`}
                      onClick={() => setSelected(f.id)}
                      className={`cursor-pointer rounded-md border p-2 text-sm ${selected === f.id ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-slate-200'} ${f.disposition === 'overridden' ? 'opacity-60' : ''}`}
                    >
                      <div className="flex flex-wrap items-center gap-1">
                        {f.jurisdictionId && (
                          <Badge>{f.jurisdictionId === 'us-fda' ? 'FDA(US)' : 'MFDS(KR)'}</Badge>
                        )}
                        {num && (
                          <span className="rounded bg-slate-800 px-1 text-[10px] font-bold text-white">
                            {num}
                          </span>
                        )}
                        <Badge tone={f.disposition === 'overridden' ? 'slate' : sevTone(f.severity)}>
                          {t(`check.sev_${f.severity}`)}
                        </Badge>
                        <span className="font-medium">{f.subject}</span>
                        {f.missing && <Badge tone="slate">{t('review.missingGroup')}</Badge>}
                        {f.location && (
                          <span className="text-xs text-slate-400">
                            {t('review.page', { p: f.location.page })}
                            {f.location.bbox && f.location.matchScore < 0.9 ? ` · ${t('review.approx')}` : ''}
                          </span>
                        )}
                        <span className="ml-auto text-xs text-slate-400">
                          {t(`check.source_${f.source}`)} · {Math.round(f.confidence * 100)}%
                        </span>
                      </div>
                      {f.quote && (
                        <div className="mt-1 rounded bg-yellow-50 px-2 py-0.5 font-mono text-xs">
                          “{f.quote}”
                        </div>
                      )}
                      <p className="mt-1">{f.finding}</p>
                      <p className="mt-1 text-slate-700">
                        <span className="font-medium">{t('check.fix')}:</span> {f.fix.what}
                      </p>
                      {f.fix.suggestedText && (
                        <div className="mt-1 rounded bg-slate-100 px-2 py-1 font-mono text-xs">
                          {f.fix.suggestedText}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-slate-500">
                        {f.citation.url ? (
                          <a
                            className="text-emerald-700 underline"
                            href={f.citation.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {f.citation.section || f.citation.kbFile}
                          </a>
                        ) : (
                          f.citation.section || f.citation.kbFile
                        )}
                        {' · '}
                        {f.citation.kbFile}
                      </div>
                      {f.severity !== 'ok' && canVerify && (
                        <div
                          className="mt-2 flex flex-wrap items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Badge
                            tone={
                              f.disposition === 'open'
                                ? 'slate'
                                : f.disposition === 'accepted'
                                  ? 'blue'
                                  : 'green'
                            }
                          >
                            {t(`check.${f.disposition}`)}
                          </Badge>
                          {f.humanNote && <span className="text-xs text-slate-500">{f.humanNote}</span>}
                          <Input
                            placeholder={t('check.noteLabel')}
                            value={itemNote[f.id] ?? ''}
                            onChange={(e) => setItemNote({ ...itemNote, [f.id]: e.target.value })}
                            className="ml-auto max-w-[14rem] text-xs"
                          />
                          {f.disposition !== 'accepted' && (
                            <Button
                              variant="secondary"
                              className="text-xs"
                              onClick={() => void dispose(f, 'accepted')}
                            >
                              {t('check.accept')}
                            </Button>
                          )}
                          {canOverride && f.disposition !== 'overridden' && (
                            <Button
                              variant="secondary"
                              className="text-xs"
                              onClick={() => void dispose(f, 'overridden')}
                            >
                              {t('check.override')}
                            </Button>
                          )}
                          {f.disposition !== 'open' && (
                            <Button
                              variant="ghost"
                              className="text-xs"
                              onClick={() => void dispose(f, 'open')}
                            >
                              {t('check.reopen')}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </Card>
          {check?.status === 'done' && (
            <Card title={t(`review.approval_${doc.approval.state}`)}>
              {doc.approval.decisionNote && (
                <p className="mb-2 text-sm text-slate-600">{doc.approval.decisionNote}</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {(doc.approval.state === 'none' || doc.approval.state === 'changes_requested') &&
                  canVerify && (
                    <Button onClick={() => void act('submit')} disabled={busy}>
                      {t('review.submit')}
                    </Button>
                  )}
                {doc.approval.state === 'submitted' && (
                  <>
                    <Input
                      placeholder={t('review.decisionNote')}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      className="max-w-xs"
                    />
                    <Button
                      onClick={() => void act('approve', { note: note || null })}
                      disabled={
                        busy || (isUploader && !sa && identity?.org?.settings.requireFourEyes !== false)
                      }
                      title={isUploader ? t('review.fourEyes') : ''}
                    >
                      {t('review.approve')}
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => void act('request-changes', { note })}
                      disabled={busy || note.trim().length < 5}
                    >
                      {t('review.requestChanges')}
                    </Button>
                  </>
                )}
                {isUploader && doc.approval.state === 'submitted' && (
                  <span className="text-xs text-amber-700">{t('review.fourEyes')}</span>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
